import React, { useState } from 'react';
import { Check, ClipboardCheck, MessageSquarePlus, Plus, X } from 'lucide-react';
import { AUTOCALIFICABLES, esCuadricula, esEleccion } from '../../../lib/formularios';
import { useEditor } from './contexto';
import { GlifoOpcion } from './controles';

/*
 * Modo "Clave de respuestas" de una pregunta en un cuestionario: puntos, respuestas correctas y
 * retroalimentación. Las preguntas que el servidor no sabe calificar solo (párrafo, archivo, fecha…)
 * guardan puntos y una retroalimentación general, y se califican a mano desde Respuestas.
 */
export default function ClaveRespuestas({ item, onListo }) {
  const { cambiarItem } = useEditor();
  const [verRetro, setVerRetro] = useState(!!(item.retro?.correcta?.texto || item.retro?.incorrecta?.texto || item.retro?.general?.texto));
  const autocalificable = AUTOCALIFICABLES.includes(item.tipo);
  const clave = item.clave || { correctas: esCuadricula(item) ? {} : [], otras_incorrectas: true };
  const setClave = (campos, claveHist) => cambiarItem(item.id, { clave: { ...clave, ...campos } }, { clave: claveHist });

  const puntos = (
    <label className="fe-clave-puntos">
      <input
        type="number"
        min="0"
        max="1000"
        inputMode="numeric"
        className="fm-campo-linea"
        value={item.puntos ?? 0}
        onChange={(e) => cambiarItem(item.id, { puntos: Math.max(0, Math.round(Number(e.target.value) || 0)) }, { clave: `${item.id}:puntos` })}
        aria-label="Puntos"
      />
      <span>{esCuadricula(item) ? 'puntos por fila' : 'puntos'}</span>
    </label>
  );

  return (
    <div className="fe-clave">
      <div className="fe-clave-encabezado">
        <span className="fe-clave-titulo">
          <ClipboardCheck />
          {autocalificable ? (item.tipo === 'texto_corto' ? 'Añade las respuestas correctas' : 'Elige las respuestas correctas') : 'Calificación manual'}
        </span>
        {puntos}
      </div>
      <div className="fm-titulo-pregunta fe-clave-pregunta">{item.titulo || 'Pregunta'}</div>

      {esEleccion(item) && <ClaveEleccion item={item} clave={clave} setClave={setClave} />}
      {item.tipo === 'texto_corto' && <ClaveTexto item={item} clave={clave} setClave={setClave} />}
      {esCuadricula(item) && <ClaveCuadricula item={item} clave={clave} setClave={setClave} />}
      {!autocalificable && (
        <p className="fe-nota">Esta pregunta no se califica sola: asígnale los puntos a cada respuesta desde la pestaña Respuestas.</p>
      )}

      {verRetro ? (
        <Retroalimentacion item={item} general={!autocalificable} />
      ) : (
        <button type="button" className="fe-btn-texto fe-clave-retro-btn" onClick={() => setVerRetro(true)}>
          <MessageSquarePlus /> Añadir retroalimentación de la respuesta
        </button>
      )}

      <div className="fe-clave-pie">
        <button type="button" className="fe-btn-borde" onClick={onListo}>Listo</button>
      </div>
    </div>
  );
}

function ClaveEleccion({ item, clave, setClave }) {
  const correctas = Array.isArray(clave.correctas) ? clave.correctas : [];
  const multiple = item.tipo === 'casillas';
  const alternar = (id) => {
    let nuevas;
    if (multiple) nuevas = correctas.includes(id) ? correctas.filter((c) => c !== id) : [...correctas, id];
    else nuevas = correctas.includes(id) ? [] : [id];
    setClave({ correctas: nuevas });
  };
  return (
    <div className="fe-clave-opciones" role="group" aria-label="Respuestas correctas">
      {(item.opciones || []).map((o, i) => {
        const ok = correctas.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            className={`fe-clave-opcion ${ok ? 'is-correcta' : ''}`}
            onClick={() => alternar(o.id)}
            aria-pressed={ok}
          >
            <GlifoOpcion tipo={item.tipo} indice={i} />
            <span className="fe-clave-opcion-texto">{o.texto}</span>
            {ok && <Check className="fe-clave-check" />}
          </button>
        );
      })}
      {item.otra && item.tipo !== 'desplegable' && (
        <div className="fe-clave-opcion is-deshabilitada">
          <GlifoOpcion tipo={item.tipo} indice={(item.opciones || []).length} />
          <span className="fe-clave-opcion-texto">Otro (se califica como incorrecta)</span>
        </div>
      )}
    </div>
  );
}

function ClaveTexto({ item, clave, setClave }) {
  const correctas = Array.isArray(clave.correctas) ? clave.correctas : [];
  const [nueva, setNueva] = useState('');
  const agregar = () => {
    const t = nueva.trim();
    if (!t || correctas.some((c) => c.toLowerCase() === t.toLowerCase())) {
      setNueva('');
      return;
    }
    setClave({ correctas: [...correctas, t] });
    setNueva('');
  };
  return (
    <div className="fe-clave-texto">
      {correctas.map((c, i) => (
        // Clave por posición: el texto no sirve de clave porque cambia al escribir (perdería el foco)
        <div key={i} className="fe-opcion-fila">
          <Check className="fe-clave-check" />
          <input
            className="fm-campo-linea"
            value={c}
            aria-label={`Respuesta correcta ${i + 1}`}
            onChange={(e) => setClave({ correctas: correctas.map((x, j) => (j === i ? e.target.value : x)) }, `${item.id}:clave:${i}`)}
          />
          <button type="button" className="fe-icono-btn" onClick={() => setClave({ correctas: correctas.filter((_, j) => j !== i) })} aria-label="Quitar respuesta" title="Quitar">
            <X />
          </button>
        </div>
      ))}
      <div className="fe-opcion-fila">
        <Plus className="fe-clave-mas" />
        <input
          className="fm-campo-linea"
          value={nueva}
          placeholder="Añadir una respuesta correcta"
          aria-label="Añadir una respuesta correcta"
          onChange={(e) => setNueva(e.target.value)}
          onBlur={agregar}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              agregar();
            }
          }}
        />
      </div>
      <label className="fe-check">
        <input
          type="checkbox"
          checked={clave.otras_incorrectas !== false}
          onChange={(e) => setClave({ otras_incorrectas: e.target.checked })}
        />
        Marcar las demás respuestas como incorrectas
      </label>
      <p className="fe-nota">No distingue mayúsculas ni espacios de más. Sin marcar, lo que no coincida queda pendiente de calificar a mano.</p>
    </div>
  );
}

function ClaveCuadricula({ item, clave, setClave }) {
  const correctas = clave.correctas && !Array.isArray(clave.correctas) ? clave.correctas : {};
  const multiple = item.tipo === 'cuadricula_casillas';
  const alternar = (fila, col) => {
    const actuales = correctas[fila] || [];
    let nuevas;
    if (multiple) nuevas = actuales.includes(col) ? actuales.filter((c) => c !== col) : [...actuales, col];
    else nuevas = actuales.includes(col) ? [] : [col];
    setClave({ correctas: { ...correctas, [fila]: nuevas } });
  };
  return (
    <div className="fe-muestra-cuadricula fm-scroll">
      <table>
        <thead>
          <tr>
            <th aria-hidden="true" />
            {(item.columnas || []).map((c) => <th key={c.id}>{c.texto}</th>)}
          </tr>
        </thead>
        <tbody>
          {(item.filas || []).map((f) => (
            <tr key={f.id}>
              <th scope="row">{f.texto}</th>
              {(item.columnas || []).map((c) => {
                const ok = (correctas[f.id] || []).includes(c.id);
                return (
                  <td key={c.id}>
                    <button
                      type="button"
                      className={`fe-clave-celda ${ok ? 'is-correcta' : ''}`}
                      onClick={() => alternar(f.id, c.id)}
                      aria-pressed={ok}
                      aria-label={`${f.texto}: ${c.texto}`}
                    >
                      {ok ? <Check /> : <span className={`fe-glifo ${multiple ? 'fe-glifo-casilla' : 'fe-glifo-radio'}`} />}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Retroalimentación: para respuestas incorrectas y correctas (las que se califican solas) o una general
 * (las de calificación manual, `retro.general`). Texto y un enlace opcional (un vídeo, un apunte…).
 */
function Retroalimentacion({ item, general }) {
  const { cambiarItem } = useEditor();
  const retro = item.retro || {};
  const set = (clave, campos) => cambiarItem(
    item.id,
    { retro: { ...retro, [clave]: { texto: '', enlace: '', ...(retro[clave] || {}), ...campos } } },
    { clave: `${item.id}:retro:${clave}:${Object.keys(campos)[0]}` },
  );
  const bloques = general
    ? [['general', 'Retroalimentación general']]
    : [['incorrecta', 'Respuestas incorrectas'], ['correcta', 'Respuestas correctas']];
  return (
    <div className="fe-retro">
      {bloques.map(([clave, titulo]) => (
        <div key={clave} className="fe-retro-bloque">
          <div className="fe-retro-titulo">{titulo}</div>
          <textarea
            className="fe-area"
            rows={2}
            value={retro[clave]?.texto || ''}
            placeholder="Escribe la retroalimentación"
            aria-label={`${titulo}: texto`}
            onChange={(e) => set(clave, { texto: e.target.value })}
          />
          <input
            className="fm-campo-linea"
            value={retro[clave]?.enlace || ''}
            placeholder="Enlace (opcional)"
            aria-label={`${titulo}: enlace`}
            onChange={(e) => set(clave, { enlace: e.target.value })}
          />
        </div>
      ))}
    </div>
  );
}
